import { computed, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';

import { toObservable } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash-es';
import { QueryPartData } from '../../../advanced-search/filters/base/entities/query-part-data.entity';
import { QueryPart } from '../../../advanced-search/filters/base/entities/query-part.class';
import { ShFacet, ShSelectedFacetValue } from '../../../elements/components/facet/entities/facet.entity';
import { DisplayMode } from '../../../overviews/generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { sortInfoToString } from '../../../overviews/generic-overview/components/display-settings-bar/helpers/sort-info.helper';
import { RequestPaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { UrlQueryState } from '../../../overviews/search-page/entities/search-page-state.entity';
import { BaseService } from '../../common/services/base.abstract.service';
import { IsLaxDateRangeQuery } from '../../dates/helpers/date-range-query-helper';
import { StructuredQuery } from '../entities/structured-query.entity';
import { ParamField, UrlQueryParams } from '../entities/url-query-params.entity';
import { FacetsToQueryParts } from '../helpers/structured-query.helper';
import { UrlQueryParamsConverter } from '../helpers/url-query-params-converter.helper';

@Injectable()
export class ShUrlQueryService extends BaseService implements OnDestroy {
    // Defaults
    private readonly DEFAULT_PAGE: number = 1;

    // Signals
    private readonly $urlParams: WritableSignal<Params> = signal({});

    private readonly $selectedFacets = computed(() =>
        UrlQueryParamsConverter.queryParamsToSelectedFacetValues(this.$urlParams())
    );

    private readonly $structuredQuery = computed(() =>
        UrlQueryParamsConverter.queryParamsToStructuredQuery(this.$urlParams())
    );

    private readonly $sortInfo = computed(() => UrlQueryParamsConverter.queryParamsToSortInfo(this.$urlParams()));

    private readonly $page = computed(() =>
        this.pageNumberToArrayIndex(UrlQueryParamsConverter.queryParamsToPage(this.$urlParams()))
    );

    private readonly $pageSize = computed(() => UrlQueryParamsConverter.queryParamsToPageSize(this.$urlParams()));

    private readonly $view = computed(() => UrlQueryParamsConverter.queryParamsToView(this.$urlParams()));

    public readonly $urlQueryState = computed(() => ({
        structuredQuery: this.$structuredQuery(),
        selectedFacets: this.$selectedFacets(),
        sortInfo: this.$sortInfo(),
        view: this.$view(),
        page: this.$page(),
        pageSize: this.$pageSize()
    }));

    private urlQueryState$: Observable<UrlQueryState>;
    private structuredQuery$: Observable<StructuredQuery>;
    private selectedFacets$: Observable<ShSelectedFacetValue[]>;

    /**
     * @deprecated
     * To be removed after DAV splitoff
     * */
    private readonly $facetQueryParts = computed(() => FacetsToQueryParts(this.$structuredQuery().Facets));
    /**
     * @deprecated
     * To be removed after DAV splitoff
     * */
    private facetQueryParts$: Observable<QueryPart[]>;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router
    ) {
        super();

        this.initQueryParamsListener();
        this.initObservables();
    }

    private initQueryParamsListener(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                filter(() => !this.hasActiveNamedOutlet()), // MED-5347 return to original page after preview
                map(() => this.activatedRoute.snapshot.queryParams),
                startWith(this.activatedRoute.snapshot.queryParams),
                distinctUntilChanged(),
                // Last 3 lines to make sure that it still triggers when navigation finished before this service is initialized
                // but also prevents double emits
                takeUntil(this.destroy$)
            )
            .subscribe(urlParams => this.$urlParams.set(urlParams));
    }

    private hasActiveNamedOutlet(route: ActivatedRoute = this.router.routerState.root): boolean {
        // Check if the current route has a named (non-primary) outlet
        if (route.outlet !== 'primary' && route.outlet !== undefined) {
            return true;
        }

        // Recursively check child routes
        return route.children.some(child => this.hasActiveNamedOutlet(child));
    }

    private initObservables(): void {
        this.urlQueryState$ = toObservable(this.$urlQueryState);
        this.structuredQuery$ = toObservable(this.$structuredQuery).pipe(shareReplay(1));
        this.facetQueryParts$ = toObservable(this.$facetQueryParts);
        this.selectedFacets$ = toObservable(this.$selectedFacets);
    }

    public getUrlQueryParams(): UrlQueryParams {
        return this.$urlParams();
    }

    public getUrlQueryState(): Observable<UrlQueryState> {
        return this.urlQueryState$;
    }

    public setUrlQueryState(urlQueryState: Partial<UrlQueryState>): void {
        const structuredQueryParams = urlQueryState.structuredQuery
            ? this.structuredQueryToQueryParams(urlQueryState.structuredQuery)
            : {};

        // Should be deprecated in favor of conversion from StructuredQuery
        const facetQueryParams = urlQueryState.selectedFacets
            ? this.selectedFacetsToQueryParams(urlQueryState.selectedFacets)
            : {};

        const pageQueryParams = urlQueryState.page ? this.pageToQueryParams(urlQueryState.page) : {};
        const pageSizeQueryParams = urlQueryState.pageSize ? this.pageSizeToQueryParams(urlQueryState.pageSize) : {};

        const sortInfoParams = urlQueryState.sortInfo ? this.sortInfoToQueryParams(urlQueryState.sortInfo) : {};
        const viewParams = urlQueryState.view ? this.displayModeToQueryParams(urlQueryState.view) : {};

        const params = {
            ...structuredQueryParams,
            ...facetQueryParams,
            ...pageQueryParams,
            ...pageSizeQueryParams,
            ...sortInfoParams,
            ...viewParams
        };

        this.setQueryParams(params, false);
    }

    // #region Get StructuredQuery
    public getStructuredQuery(): Observable<StructuredQuery> {
        return this.structuredQuery$.pipe(distinctUntilChanged(isEqual));
    }
    // #endregion

    // #region Set StructuredQuery
    public setStructuredQuery(structuredQuery: StructuredQuery): void {
        const params = this.structuredQueryToQueryParams(structuredQuery);

        this.setQueryParams(params, true);
    }

    private structuredQueryToQueryParams(structuredQuery: StructuredQuery): UrlQueryParams {
        const urlQueryParams: UrlQueryParams = UrlQueryParamsConverter.generateQueryParams(structuredQuery);
        const facetQueryParam: string | undefined = undefined; // Don't set facets this way for now

        const params: UrlQueryParams = {
            [ParamField.Global]: urlQueryParams.q !== '' ? urlQueryParams.q : undefined,
            [ParamField.Semantic]: urlQueryParams.sq !== '' ? urlQueryParams.sq : undefined,
            [ParamField.FullText]: urlQueryParams.ftq !== '' ? urlQueryParams.ftq : undefined,
            [ParamField.AdvancedSearch]: urlQueryParams.advQ !== '' ? urlQueryParams.advQ : undefined,
            [ParamField.Facets]: facetQueryParam,
            [ParamField.Fields]: urlQueryParams.fields !== '' ? urlQueryParams.fields : undefined
        };

        return params;
    }
    // #endregion

    // #region Get Facets

    /**
     * @deprecated
     * To be removed after DAV splitoff
     * */
    public getFacetQueryParts(): Observable<QueryPart[]> {
        return this.facetQueryParts$.pipe(distinctUntilChanged(isEqual));
    }

    public getSelectedFacets(): Observable<ShSelectedFacetValue[]> {
        return this.selectedFacets$.pipe(distinctUntilChanged(isEqual));
    }
    // #endregion

    // #region Set Facets
    public addFacetValue(facetValue: ShSelectedFacetValue): void {
        const currentSelected: ShSelectedFacetValue[] = this.$selectedFacets();

        if (currentSelected.includes(facetValue)) {
            this.removeFacetValue(facetValue);
        } else {
            const isDate = IsLaxDateRangeQuery(facetValue.value);

            if (isDate) {
                // Show only 1 selected date-facet value at the time
                const withoutValuesOfFacet: ShSelectedFacetValue[] = currentSelected.filter(
                    selected => selected.key !== facetValue.key
                );
                const newSelected: ShSelectedFacetValue[] = [...withoutValuesOfFacet, facetValue];
                return this.setSelectedFacets(newSelected);
            }

            return this.setSelectedFacets([...currentSelected, facetValue]);
        }
    }

    public replaceFacetValues(facet: ShFacet, selectedValues: ShSelectedFacetValue[]): void {
        const currentSelected: ShSelectedFacetValue[] = this.$selectedFacets();

        // Remove originally selected values of this facet
        const withoutValuesOfFacet: ShSelectedFacetValue[] = currentSelected.filter(
            selected => selected.key !== facet.key
        );

        // Add new selected values
        const newSelected: ShSelectedFacetValue[] = [...withoutValuesOfFacet, ...selectedValues];

        this.setSelectedFacets(newSelected);
    }

    public removeFacetValue(facet: ShSelectedFacetValue): void {
        const currentSelected: ShSelectedFacetValue[] = this.$selectedFacets();

        const newSelected: ShSelectedFacetValue[] = currentSelected.filter(selected => {
            return !(facet.key === selected.key && facet.value === selected.value);
        });

        this.setSelectedFacets(newSelected);
    }

    private setSelectedFacets(selectedFacets: ShSelectedFacetValue[]): void {
        const params = this.selectedFacetsToQueryParams(selectedFacets);
        this.setQueryParams(params, true);
    }

    private selectedFacetsToQueryParams(selectedFacets: ShSelectedFacetValue[]): UrlQueryParams {
        // Generate new facet url query
        const facetQueryParam = UrlQueryParamsConverter.selectedFacetsToFacetQueryParamValue(selectedFacets);
        const paramValue = facetQueryParam === '' ? undefined : facetQueryParam;

        return {
            [ParamField.Facets]: paramValue
        };
    }
    // #endregion

    // #region Set Advanced Search
    public setAdvancedSearchQueryParam(structuredQuery: StructuredQuery): void {
        // Generate new advanced search url query
        const urlQueryParams: UrlQueryParams = UrlQueryParamsConverter.generateQueryParams(structuredQuery);

        // Needed to specifically set only advanced search related params and undefined others to retrigger search
        const params: UrlQueryParams = {
            [ParamField.Global]: urlQueryParams.q !== '' ? urlQueryParams.q : undefined,
            [ParamField.Semantic]: urlQueryParams.sq !== '' ? urlQueryParams.sq : undefined,
            [ParamField.FullText]: urlQueryParams.ftq !== '' ? urlQueryParams.ftq : undefined,
            [ParamField.AdvancedSearch]: urlQueryParams.advQ !== '' ? urlQueryParams.advQ : undefined
        };

        this.setQueryParams(params, true);
    }
    // #endregion

    // #region Get Fields
    public getFieldsQuery(): Observable<QueryPartData[]> {
        return this.structuredQuery$.pipe(
            map(structuredQuery => structuredQuery.Fields),
            shareReplay(1)
        );
    }
    // #endregion

    // #region Set Fields
    public setFieldsQuery(queryParts: QueryPartData[]): void {
        const fieldsQueryParam = UrlQueryParamsConverter.queryPartsToQueryParam(queryParts);

        this.setQueryParams(
            {
                [ParamField.Fields]: fieldsQueryParam ? fieldsQueryParam : undefined
            },
            true
        );
    }
    // #endregion

    // #region Set page settings
    public setPaginationInfo(paginationInfo: RequestPaginationInfo): void {
        this.setQueryParams(
            {
                ...this.pageToQueryParams(paginationInfo.page),
                ...this.pageSizeToQueryParams(paginationInfo.resultsPerPage)
            },
            false
        );
    }

    public setPageSize(pageSize: number): void {
        const params = this.pageSizeToQueryParams(pageSize);
        this.setQueryParams(params, true);
    }

    public setPage(page: number): void {
        const params = this.pageToQueryParams(page);
        this.setQueryParams(params, true);
    }

    private pageToQueryParams(page: number): UrlQueryParams {
        const pageNumber = this.arrayIndexToPageNumber(page);
        const pageValue = pageNumber > this.DEFAULT_PAGE ? pageNumber.toString() : undefined;

        return {
            [ParamField.Page]: pageValue
        };
    }

    private pageSizeToQueryParams(pageSize: number): UrlQueryParams {
        const paramValue = pageSize.toString();

        return {
            [ParamField.PageSize]: paramValue
        };
    }

    private arrayIndexToPageNumber(page: number) {
        return page + 1;
    }

    private pageNumberToArrayIndex(page: number = this.DEFAULT_PAGE) {
        const arrayIndex = page - 1;
        if (page < 0) {
            return 0;
        }
        return arrayIndex;
    }

    public setSortInfo(sortInfo: SortInfo): void {
        const params = this.sortInfoToQueryParams(sortInfo);

        this.setQueryParams(params, true);
    }

    private sortInfoToQueryParams(sortInfo: SortInfo): UrlQueryParams {
        const paramValue = sortInfoToString(sortInfo);

        return {
            [ParamField.Sort]: paramValue
        };
    }

    public setDisplayMode(displayMode: DisplayMode): void {
        const params = this.displayModeToQueryParams(displayMode);
        this.setQueryParams(params, false);
    }

    private displayModeToQueryParams(displayMode: DisplayMode): UrlQueryParams {
        return {
            [ParamField.View]: displayMode
        };
    }
    // #endregion

    // #region Set URL param
    private setQueryParams(params: UrlQueryParams, resetPage: boolean): void {
        // Reset pagination on Query and Sort changes
        if (resetPage) {
            params[ParamField.Page] = undefined;
        }

        this.router.navigate([], {
            queryParamsHandling: 'merge',
            queryParams: params,
            relativeTo: this.activatedRoute,
            replaceUrl: true
        });
    }
    // #endregion

    // #region Clear facet query
    public clearFacetQuery(): void {
        this.setQueryParams(
            {
                [ParamField.Facets]: undefined
            },
            true
        );
    }
    // #endregion
}
